import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { apiService } from '../../services/api';
import { phaseShortLabel, statusColorClasses } from '../../utils/trialTrackerTimeline';

interface EmergingTrialRow {
  id: number;
  nct_id: string;
  trial_name?: string | null;
  full_title?: string | null;
  phases?: string | null;
  overall_status?: string | null;
  enrollment_size?: string | null;
  study_start_date?: string | null;
  primary_completion_date?: string | null;
  study_completion_date?: string | null;
  sponsor?: string | null;
}

interface EmergingTrialGroup {
  indication_code: string;
  indication_name: string;
  trials: EmergingTrialRow[];
}

interface SocEntry {
  id: number;
  pmid?: number | null;
  identifier?: string | null;
  trial_arm?: string | null;
  orr_exp?: number | null;
  mpfs_exp_mos?: number | null;
  mos_exp_mos?: number | null;
  exp_n?: number | null;
}

interface SocGroup {
  indication_details: string;
  count: number;
  entries: SocEntry[];
}

interface AssetDetail {
  id: number;
  asset_name: string;
  modality?: string | null;
  target?: string | null;
  company_name?: string | null;
  other_names?: string[] | null;
  pmids?: number[] | null;
}

interface Props {
  assetId: number;
  onClose: () => void;
}

const AssetDrawer: React.FC<Props> = ({ assetId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [emergingTrials, setEmergingTrials] = useState<EmergingTrialGroup[]>([]);
  const [socHistory, setSocHistory] = useState<SocGroup[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.fetchAssetTrackerDetail(assetId)
      .then((res) => {
        if (cancelled) return;
        if (res?.success) {
          setAsset(res.asset);
          setEmergingTrials(res.emerging_trials || []);
          setSocHistory(res.soc_history || []);
        } else {
          setError(res?.error || 'Failed to load asset detail.');
        }
      })
      .catch(() => { if (!cancelled) setError('Failed to load asset detail.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assetId]);

  return (
    <div className="w-80 flex-none border-l border-slate-200 bg-slate-50 flex flex-col h-full">
      <div className="flex-1 min-w-0 overflow-y-auto p-4">
        <div className="flex items-start justify-between mb-2.5">
          <span className="text-[10.5px] font-bold tracking-wide text-slate-400">ASSET</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={15} /></button>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <div className="text-sm text-rose-600">{error}</div>
        ) : asset ? (
          <>
            <div className="font-serif text-xl font-bold leading-tight">{asset.asset_name}</div>
            <div className="text-xs text-slate-400 mt-0.5">{asset.company_name || '—'}</div>
            <div className="flex flex-wrap gap-1.5 mt-2.5 mb-3">
              {asset.target && (
                <span className="text-[10.5px] font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">{asset.target}</span>
              )}
              {asset.modality && (
                <span className="text-[10.5px] font-semibold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700">{asset.modality}</span>
              )}
            </div>
            {asset.other_names && asset.other_names.length > 0 && (
              <div className="text-[11px] text-slate-500 mb-3">Also known as: {asset.other_names.join(', ')}</div>
            )}

            <div className="text-[10.5px] font-bold tracking-wide text-slate-400 mt-4 mb-2">
              CLINICAL TRIALS (EMERGING) — {emergingTrials.reduce((n, g) => n + g.trials.length, 0)}
            </div>
            {emergingTrials.length === 0 && (
              <div className="text-[11px] text-slate-400 mb-3">No matching trials found in the emerging-trial system.</div>
            )}
            {emergingTrials.map((group) => (
              <div key={group.indication_code} className="mb-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-bold text-slate-500">{group.indication_name}</span>
                  <span className="text-[10px] text-slate-400">{group.trials.length}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="flex flex-col gap-1.5">
                  {group.trials.map((t) => {
                    const status = statusColorClasses(t.overall_status);
                    return (
                      <div key={t.id} className="bg-white border border-slate-200 rounded-lg px-2.5 py-2">
                        <div className="text-[12px] font-semibold leading-snug">{t.trial_name || t.full_title || t.nct_id}</div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="text-[9.5px] font-mono text-slate-400">{t.nct_id}</span>
                          <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{phaseShortLabel(t.phases || undefined)}</span>
                          <span className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded ${status.bg} ${status.fg}`}>{t.overall_status || '—'}</span>
                          {t.enrollment_size && <span className="text-[9.5px] text-slate-400">n={t.enrollment_size}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="text-[10.5px] font-bold tracking-wide text-slate-400 mt-4 mb-2 pt-3 border-t border-slate-200">
              ESTABLISHED INDICATIONS (SOC) — {socHistory.reduce((n, g) => n + g.count, 0)}
            </div>
            {socHistory.length === 0 && (
              <div className="text-[11px] text-slate-400">No established SOC data found for this asset.</div>
            )}
            {socHistory.map((group) => (
              <div key={group.indication_details} className="mb-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-bold text-slate-500">{group.indication_details}</span>
                  <span className="text-[10px] text-slate-400">{group.count}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="flex flex-col gap-1.5">
                  {group.entries.map((e) => (
                    <div key={e.id} className="bg-white border border-slate-200 rounded-lg px-2.5 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap text-[10.5px]">
                        {e.identifier && <span className="font-mono text-slate-500">{e.identifier}</span>}
                        {e.pmid && <span className="text-slate-400">PMID {e.pmid}</span>}
                        {e.trial_arm && <span className="text-slate-500">{e.trial_arm}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10.5px] text-slate-600">
                        {e.orr_exp != null && <span>ORR {e.orr_exp}%</span>}
                        {e.mpfs_exp_mos != null && <span>mPFS {e.mpfs_exp_mos}mo</span>}
                        {e.mos_exp_mos != null && <span>mOS {e.mos_exp_mos}mo</span>}
                        {e.exp_n != null && <span>n={e.exp_n}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AssetDrawer;
